import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScanSearch,
  FileSearch,
  Scale,
  Monitor,
  Plus,
  Clock,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { ComplianceScore } from '@/components/dashboard/ComplianceScore';
import { dashboardAPI, scansAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useTranslation } from '@/i18n/TranslationContext';
import { cn } from '@/lib/utils';

const statusStyleMap = {
  completed: 'border-vrs-green/30 text-vrs-green bg-vrs-green/10',
  running: 'border-vrs-cyan/30 text-vrs-cyan bg-vrs-cyan/10',
  pending: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
  failed: 'border-vrs-red/30 text-vrs-red bg-vrs-red/10',
};
const statusKeys = { completed: 'status_completed', running: 'status_running', pending: 'status_pending', failed: 'status_failed' };

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsRes, scansRes] = await Promise.all([
          dashboardAPI.getStats(),
          scansAPI.getRecent(5),
        ]);
        setStats(statsRes.data);
        setRecentScans(scansRes.data.scans || []);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError('Unable to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
            {t('dashboard')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('welcome_back')}{user?.name ? `, ${user.name}` : ''}. {t('compliance_overview')}
          </p>
        </div>
        <Button
          data-testid="dashboard-new-scan-button"
          className="font-semibold active:scale-[0.98] transition-[background-color,border-color,transform] duration-200"
          onClick={() => navigate('/scans/run')}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('new_scan')}
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <KpiCard title={t('compliance_score')} value={stats?.compliance_score || 0} subtitle={t('overall_average')} icon={ScanSearch} />
        <KpiCard title={t('assets_monitored')} value={stats?.assets_monitored || 0} subtitle={t('total_scanned')} icon={Monitor} />
        <KpiCard title={t('open_findings')} value={stats?.open_findings || 0} subtitle={t('across_all_scans')} icon={FileSearch} />
        <KpiCard title={t('regulations_mapped')} value={stats?.regulations_mapped || 0} subtitle={t('active_law_benchmarks')} icon={Scale} />
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 lg:gap-6">
        {/* Recent Scans - 8 cols */}
        <Card className="xl:col-span-8 border-border/60">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
              {t('recent_scans')}
            </CardTitle>
            <Button variant="ghost" size="sm" data-testid="dashboard-view-all-scans" className="text-xs text-muted-foreground hover:text-foreground">
              {t('view_all')} <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentScans.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs">{t('target_url')}</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">{t('status')}</TableHead>
                    <TableHead className="text-xs">{t('date_issued')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentScans.map((scan) => (
                    <TableRow key={scan.scan_id} data-testid="recent-scan-row" className="cursor-pointer hover:bg-muted/30">
                      <TableCell className="font-medium text-sm">
                        <div className="flex items-center gap-2">
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          {scan.target_url || '\u2014'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-[10px] capitalize">{scan.scan_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-[10px]', statusStyleMap[scan.status] || statusStyleMap.pending)}>
                          {t(statusKeys[scan.status] || 'status_pending')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">{formatDate(scan.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyScansState />
            )}
          </CardContent>
        </Card>

        {/* Compliance Overview - 4 cols */}
        <Card className="xl:col-span-4 border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
              {t('compliance_overview_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <ComplianceScore
              score={stats?.compliance_score || 0}
              lastUpdated={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            />
            <div className="space-y-3">
              <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                {t('by_category')}
              </p>
              {['Accessibility', 'Cybersecurity', 'Privacy', 'Data Protection'].map((cat) => (
                <div key={cat} className="flex items-center justify-between">
                  <span className="text-sm text-foreground/80">{cat}</span>
                  <Badge variant="outline" className="text-[10px] text-muted-foreground border-border">{t('no_data')}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyScansState() {
  const { t } = useTranslation();
  return (
    <div data-testid="empty-scans-state" className="flex flex-col items-center justify-center py-12 space-y-3">
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted/50 border border-border">
        <ScanSearch className="h-7 w-7 text-muted-foreground" />
      </div>
      <div className="text-center space-y-1">
        <p className="text-sm font-medium text-foreground">{t('no_scans_yet')}</p>
        <p className="text-xs text-muted-foreground max-w-[240px]">{t('start_first_scan')}</p>
      </div>
      <Button size="sm" className="mt-2 font-semibold" data-testid="empty-state-new-scan">
        <Plus className="mr-2 h-4 w-4" />{t('start_first_scan_btn')}
      </Button>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-10 w-28" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="border-border/60">
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-3" />
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 lg:gap-6">
        <Card className="xl:col-span-8 border-border/60">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-4 w-32" />
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
        <Card className="xl:col-span-4 border-border/60">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-2 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
